import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useDrag } from 'react-dnd';
import FlyerTemplate from './FlyerTemplate';
import { useAppState } from '../../hooks/useAppState';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './FlyerTemplateManager.css';

// Icons as inline SVGs
const MergeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="3" width="8" height="8" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
    <rect x="13" y="13" width="8" height="8" rx="1" />
    <path d="M7 11v2M17 11v2M11 7h2M11 17h2" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15,18 9,12 15,6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9,18 15,12 9,6" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

function FlyerTemplateManager() {
  const { getAccessTokenSilently } = useAuth0();
  const { organizationId } = useAppState();
  const notification = useNotification();
  const [templates, setTemplates] = useState([]);
  const [grid, setGrid] = useState(Array.from({ length: 12 }, () => ({
    content: null,
    dimensions: { width: 400, height: 400 },
    mergedCells: [],
    spanWidth: 1,
    spanHeight: 1,
    templateWidth: 400,
    templateHeight: 400
  })));

  const [templateName, setTemplateName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [flyerEditing, setFlyerEditing] = useState(false);
  const [editingFlyerTemplateId, setFlyerEditingTemplateId] = useState(null);
  const [pages, setPages] = useState([Array.from({ length: 12 }, () => ({
    content: null,
    dimensions: { width: 400, height: 400 },
    mergedCells: [],
    spanWidth: 1,
    spanHeight: 1,
    templateWidth: 400,
    templateHeight: 400
    }))]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [gridDimensions, setGridDimensions] = useState('4x3');
  const [selectedCells, setSelectedCells] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [showCenterGuides, setShowCenterGuides] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Page configs & sections
  const [pageConfigs, setPageConfigs] = useState([{}]);
  const [sections, setSections] = useState([]);
  const [cellConfigs, setCellConfigs] = useState([]);
  // Cell config editing
  const [editingCellIndex, setEditingCellIndex] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state && location.state.flyerTemplate) {
        const flyerTemplateData = JSON.parse(location.state.flyerTemplate.data);
        setGrid(flyerTemplateData[0]);
        if(flyerTemplateData[0].length > 12)
        {
          setGridDimensions('4x4')
        } else {
          setGridDimensions('4x3')
        }
        setCurrentPageIndex(0);
        setPages(flyerTemplateData);
        setFlyerEditing(true);
        setFlyerEditingTemplateId(location.state.flyerTemplate._id || location.state.flyerTemplate.id);
        setTemplateName(location.state.flyerTemplate.name || '');
        setIsDefault(location.state.flyerTemplate.is_default || false);

        // Load enhanced fields
        if (location.state.flyerTemplate.page_configs) setPageConfigs(location.state.flyerTemplate.page_configs);
        if (location.state.flyerTemplate.sections) setSections(location.state.flyerTemplate.sections);
        if (location.state.flyerTemplate.cell_configs) setCellConfigs(location.state.flyerTemplate.cell_configs);
      }
    }, [location]);

  useEffect(() => {
    fetchAdTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, organizationId]);

  useEffect(() => {
    const updateCurrentPageInPagesState = () => {
      setPages(prevPages => {
        const updatedPages = [...prevPages];
        updatedPages[currentPageIndex] = [...grid];
        return updatedPages;
      });
    };
    updateCurrentPageInPagesState();
  }, [grid, currentPageIndex]);

  const handleGridDimensionsChange = (event) => {
    setGridDimensions(event.target.value);
    setSelectedCells([]);
    switch(event.target.value) {
      case '4x3':
        setGrid(Array.from({ length: 12 }, () => ({
          content: null,
          dimensions: { width: 400, height: 400 },
          mergedCells: [],
          spanWidth: 1,
          spanHeight: 1,
          templateWidth: 400,
          templateHeight: 400
        })));
        break;
      case '4x4':
        setGrid(Array.from({ length: 16 }, () => ({
          content: null,
          dimensions: { width: 300, height: 400 },
          mergedCells: [],
          spanWidth: 1,
          spanHeight: 1,
          templateWidth: 300,
          templateHeight: 400
        })));
        break;
      default:
        break;
    }
  }

  const fetchAdTemplates = async () => {
    if (!organizationId) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/templates/organization/${organizationId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleCancel = () => {
    navigate('/flyertemplates');
  };

  const handleDelete = (event) => {
    event.preventDefault();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    DeleteFlyerTemplate(editingFlyerTemplateId);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleSave = (event) => {
    event.preventDefault();
    saveFlyerTemplate(event);
  }

  const handleSaveAndClose = async (event) => {
    event.preventDefault();
    await saveFlyerTemplate(event);
    handleCancel();
  }

  const handleFlyerTemplatePreviewImageUpload = (file) => {
    return new Promise(async (resolve, reject) => {
      const token = await getAccessTokenSilently();
      if (!file) {
        resolve(null);
        return;
      }
      const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/s3/signed-url?type=flyer-template&fileName=${encodeURIComponent(file.name)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const { data: { signedUrl } } = response;
      if (signedUrl) {
        const result = await axios.put(signedUrl, file, {
          headers: {
            'Content-Type': file.type
          }
        });
        if (result.status === 200) {
          const imageUrl = signedUrl.split("?")[0];
          resolve(imageUrl);
        } else {
          console.error('Error uploading file:', result);
          reject('Error uploading file.');
        }
      } else {
        console.error('Server did not return a signed URL for upload.');
        reject('Server did not return a signed URL for upload.');
      }
    });
  };

  const saveFlyerTemplate = async (event) => {
    event.preventDefault();
    const token = await getAccessTokenSilently();
    return new Promise(async (resolve, reject) => {
      // Always use page 1 (index 0) for preview image
      const originalPageIndex = currentPageIndex;
      if (currentPageIndex !== 0 && pages.length > 0) {
        setGrid(pages[0]);
        setCurrentPageIndex(0);
        // Wait for React to re-render with page 0
        await new Promise(r => setTimeout(r, 100));
      }

      const flyerTemplate = document.getElementById('flyer-template');
      const canvas = await html2canvas(flyerTemplate);
      const imgData = canvas.toDataURL();
      const response1 = await fetch(imgData);
      const blob = await response1.blob();
      let previewImageFile = new File([blob], "preview.png", {
        type: "image/png",
      });
      const uploadedImageUrl = await handleFlyerTemplatePreviewImageUpload(previewImageFile);

      // Restore original page if we switched
      if (originalPageIndex !== 0 && pages.length > 0) {
        setGrid(pages[originalPageIndex]);
        setCurrentPageIndex(originalPageIndex);
      }
      const gridData = JSON.stringify(pages);
      try {
        const url = flyerEditing ?
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/flyertemplates/${editingFlyerTemplateId}` :
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/flyertemplates/`;
        const method = flyerEditing ? 'patch' : 'post';
        const requestBody = {
          name: templateName,
          data: gridData,
          preview_image_URL: uploadedImageUrl,
          organization_id: organizationId,
          is_default: isDefault,
        };
        // Include enhanced fields if any are set
        if (pageConfigs.some(pc => pc && Object.keys(pc).length > 0)) requestBody.page_configs = pageConfigs;
        if (sections.length > 0) requestBody.sections = sections;
        if (cellConfigs.length > 0) requestBody.cell_configs = cellConfigs;

        const response2 = await axios[method](url, requestBody, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if(response2.status === 200){
          if(flyerEditing) {
            notification.success('Flyer template was successfully updated.');
          } else if(!flyerEditing) {
            notification.success('Flyer template was successfully saved.');
            setFlyerEditing(true);
            setFlyerEditingTemplateId(response2.data.result.id);
          }
          resolve();
        }
      } catch (error) {
        notification.error('Error occurred while saving the template.');
        reject(new Error('Error saving template:', error));
      }
    });
  };

  const DeleteFlyerTemplate = async (flyerTemplateId) => {
    const token = await getAccessTokenSilently();
    try {
      const response = await axios.delete(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyertemplates/${flyerTemplateId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if(response.status === 200) {
        notification.success('Template was successfully deleted.');

        if (flyerEditing && flyerTemplateId === editingFlyerTemplateId) {
          setFlyerEditing(false);
          setFlyerEditingTemplateId(null);
          handleCancel();
        }
      }
    }  catch (error) {
      notification.error('Error occurred while deleting the template.');
    }
  }

  const areCellsAdjacent = (selectedCells, gridDimensions) => {
    const cols = parseInt(gridDimensions.split('x')[1], 10);

    let allSelectedCells = [...selectedCells];
    selectedCells.forEach(cell => {
        allSelectedCells = [...allSelectedCells, ...grid[cell].mergedCells];
    });
    allSelectedCells = Array.from(new Set(allSelectedCells));

    const minRow = Math.min(...allSelectedCells.map(cell => Math.floor(cell / cols)));
    const maxRow = Math.max(...allSelectedCells.map(cell => Math.floor(cell / cols)));
    const minCol = Math.min(...allSelectedCells.map(cell => cell % cols));
    const maxCol = Math.max(...allSelectedCells.map(cell => cell % cols));

    for (let row = minRow; row <= maxRow; row++) {
        for (let col = minCol; col <= maxCol; col++) {
            const cellIndex = row * cols + col;
            if (!allSelectedCells.includes(cellIndex)) {
                return false;
            }
        }
    }
    return true;
  };

  const mergeSelectedCells =  () => {
    const cols = parseInt(gridDimensions.split('x')[1], 10);

    if (selectedCells.length <= 1) {
        notification.warning("Select at least two cells to merge.");
        return;
    }

    if (!areCellsAdjacent(selectedCells, gridDimensions)) {
        notification.warning("Only adjacent cells can be merged.");
        return;
    }

    const primaryCellIndex = Math.min(...selectedCells);
    const secondaryCellIndices = selectedCells.filter(index => index !== primaryCellIndex);

    setGrid(prevGrid => {
        const newGrid = [...prevGrid];

        newGrid[primaryCellIndex].mergedCells = Array.from(new Set([
            ...newGrid[primaryCellIndex].mergedCells,
            ...secondaryCellIndices
        ]));

        secondaryCellIndices.forEach(index => {
            if (newGrid[index].mergedCells.length > 0) {
                newGrid[primaryCellIndex].mergedCells = Array.from(new Set([
                    ...newGrid[primaryCellIndex].mergedCells,
                    ...newGrid[index].mergedCells
                ]));
            }
        });

        newGrid[primaryCellIndex].dimensions.width = 0;
        newGrid[primaryCellIndex].dimensions.height = 0;
        newGrid[primaryCellIndex].spanWidth = 0;
        newGrid[primaryCellIndex].spanHeight = 0;

        selectedCells.forEach(index => {
            if (index !== primaryCellIndex) {
                newGrid[index].content = null;
                newGrid[index].dimensions = { width: 0, height: 0 };
                newGrid[index].mergedCells = [];
                newGrid[index].spanWidth = 0;
                newGrid[index].spanHeight = 0;
            }
        });

        const allCellsToConsider = [primaryCellIndex, ...newGrid[primaryCellIndex].mergedCells, ...secondaryCellIndices];

        const minCol = Math.min(...allCellsToConsider.map(cell => cell % cols));
        const maxCol = Math.max(...allCellsToConsider.map(cell => cell % cols));
        newGrid[primaryCellIndex].spanWidth = maxCol - minCol + 1;

        const minRow = Math.min(...allCellsToConsider.map(cell => Math.floor(cell / cols)));
        const maxRow = Math.max(...allCellsToConsider.map(cell => Math.floor(cell / cols)));
        newGrid[primaryCellIndex].spanHeight = maxRow - minRow + 1;

        newGrid[primaryCellIndex].dimensions.height = 400 * newGrid[primaryCellIndex].spanHeight;

        switch(cols) {
            case 4:
                newGrid[primaryCellIndex].dimensions.width = 300 * newGrid[primaryCellIndex].spanWidth;
                break;
            default:
                newGrid[primaryCellIndex].dimensions.width = 400 * newGrid[primaryCellIndex].spanWidth;
        }
        return newGrid;
    });

    setSelectedCells([]);
  };

  const handlePreviousPage = () => {
    setCurrentPageIndex(prevIndex => {
      const newIndex = prevIndex - 1;
      setGrid(pages[newIndex]);
      if(pages[newIndex].length > 12) {
        setGridDimensions('4x4')
      } else {
        setGridDimensions('4x3')
      }
      return newIndex;
    });
    setSelectedCells([]);
  };

  const handleNextPage = () => {
    setCurrentPageIndex(prevIndex => {
      const newIndex = prevIndex + 1;
      setGrid(pages[newIndex]);
      if(pages[newIndex].length > 12) {
        setGridDimensions('4x4')
      } else {
        setGridDimensions('4x3')
      }
      return newIndex;
    });
    setSelectedCells([]);
  };

  // --- Page Config helpers ---
  const currentPageConfig = pageConfigs[currentPageIndex] || {};
  const updatePageConfig = (key, value) => {
    setPageConfigs(prev => {
      const updated = [...prev];
      while (updated.length <= currentPageIndex) updated.push({});
      updated[currentPageIndex] = { ...updated[currentPageIndex], [key]: value };
      return updated;
    });
  };

  // --- Cell Config helpers ---
  const getCellConfig = (cellIndex) => {
    if (!cellConfigs[currentPageIndex]) return {};
    return cellConfigs[currentPageIndex][cellIndex] || {};
  };
  const updateCellConfig = (cellIndex, key, value) => {
    setCellConfigs(prev => {
      const updated = [...prev];
      while (updated.length <= currentPageIndex) updated.push([]);
      if (!updated[currentPageIndex]) updated[currentPageIndex] = [];
      const pageArr = [...updated[currentPageIndex]];
      while (pageArr.length <= cellIndex) pageArr.push({});
      pageArr[cellIndex] = { ...pageArr[cellIndex], [key]: value };
      updated[currentPageIndex] = pageArr;
      return updated;
    });
  };

  // --- Section helpers ---
  const addSection = () => {
    setSections(prev => [...prev, {
      section_id: `section-${Date.now()}`,
      name: '',
      category: '',
      page_index: currentPageIndex,
      cell_indices: [],
      header_style: { background_color: '#f97316', text_color: '#ffffff', font_size: 16 },
    }]);
  };

  const updateSection = (idx, key, value) => {
    setSections(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [key]: value };
      return updated;
    });
  };

  const removeSection = (idx) => {
    setSections(prev => prev.filter((_, i) => i !== idx));
  };

  const currentPageSections = sections.filter(s => s.page_index === currentPageIndex);

  // Handle cell click for editing cell config
  const handleCellConfigClick = (cellIndex) => {
    setEditingCellIndex(editingCellIndex === cellIndex ? null : cellIndex);
  };

  const handleAddPage = () => {
    setPages(prevPages => {
      const newPage = (Array.from({ length: 12 }, () => ({
        content: null,
        dimensions: { width: 400, height: 400 },
        mergedCells: [],
        spanWidth: 1,
        spanHeight: 1,
        templateWidth: 400,
        templateHeight: 400
      })));
      const newPages = [...prevPages, newPage];
      setCurrentPageIndex(newPages.length - 1);
      setGrid(newPage);
      setGridDimensions('4x3');
      return newPages;
    });
    setSelectedCells([]);
  };

  const handleDeletePage = () => {
    setPages(prevPages => {
      const updatedPages = [...prevPages];
      updatedPages.splice(currentPageIndex, 1);
      const newIndex = Math.max(currentPageIndex - 1, 0);
      setCurrentPageIndex(newIndex);
      setGrid(updatedPages[newIndex]);
      if(updatedPages[newIndex].length > 12) {
        setGridDimensions('4x4')
      } else {
        setGridDimensions('4x3')
      }
      return updatedPages;
    });
    setSelectedCells([]);
  };

  return (
    <div className="flyer-template-editor">

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete this template?"
        description="This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Main Toolbar */}
      <div className="editor-toolbar">
        {/* Template Name */}
        <div className="toolbar-section">
          <label className="toolbar-label">Template Name</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter template name"
            className="toolbar-input"
            style={{ width: '180px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' }}
          />
        </div>
        <div className="toolbar-section">
          <label className="toolbar-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            Default for WhatsApp
          </label>
        </div>
        <div className="toolbar-divider" />
        {/* Page Dimensions Selector */}
        <div className="toolbar-section">
          <label className="toolbar-label">Page Dimensions</label>
          <select
            value={gridDimensions || '4x3'}
            onChange={handleGridDimensionsChange}
            className="toolbar-select"
          >
            <option value="4x3">4x3</option>
            <option value="4x4">4x4</option>
          </select>
        </div>

        <div className="toolbar-divider" />

        {/* Cell Actions */}
        <div className="toolbar-section">
          <button
            onClick={mergeSelectedCells}
            disabled={selectedCells.length < 2}
            className="control-btn merge-btn"
            title="Merge Selected Cells"
          >
            <MergeIcon />
            <span className="btn-label">Merge Cells</span>
          </button>
        </div>

        <div className="toolbar-spacer" />

        {/* Page Navigation */}
        <div className="toolbar-section page-navigation">
          <button
            onClick={handlePreviousPage}
            disabled={currentPageIndex === 0}
            className="nav-btn"
            title="Previous Page"
          >
            <ChevronLeftIcon />
          </button>
          <span className="page-indicator">
            Page {currentPageIndex + 1} of {pages.length}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPageIndex === pages.length - 1}
            className="nav-btn"
            title="Next Page"
          >
            <ChevronRightIcon />
          </button>
        </div>

        <div className="toolbar-divider" />

        {/* Page Actions */}
        <div className="toolbar-section page-actions">
          <button
            onClick={handleAddPage}
            className="control-btn add-page-btn"
            title="Add Page"
          >
            <PlusIcon />
            <span className="btn-label">Add Page</span>
          </button>
          <button
            onClick={handleDeletePage}
            disabled={pages.length <= 1}
            className="control-btn delete-page-btn"
            title="Delete Page"
          >
            <TrashIcon />
            <span className="btn-label">Delete Page</span>
          </button>
        </div>

        <div className="toolbar-divider" />

        {/* Zoom Controls */}
        <div className="toolbar-section zoom-controls">
          <button
            className="zoom-btn"
            onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
            disabled={zoom <= 0.5}
            title="Zoom Out"
          >
            −
          </button>
          <input
            type="range"
            className="zoom-slider"
            min="0.5"
            max="2"
            step="0.1"
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
          />
          <span className="zoom-level">{Math.round(zoom * 100)}%</span>
          <button
            className="zoom-btn"
            onClick={() => setZoom(Math.min(2, zoom + 0.1))}
            disabled={zoom >= 2}
            title="Zoom In"
          >
            +
          </button>
        </div>

        {/* Guide Controls */}
        <div className="toolbar-section guide-controls">
          <button
            className={`guide-btn ${showCenterGuides ? 'active' : ''}`}
            onClick={() => setShowCenterGuides(!showCenterGuides)}
            title="Toggle Center Guidelines"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2">
              <line x1="12" y1="2" x2="12" y2="22" />
              <line x1="2" y1="12" x2="22" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Page Config Bar */}
      <div className="page-config-bar">
        <div className="cell-prop-group">
          <label>Page Role</label>
          <select value={currentPageConfig.page_role || 'inner'}
            onChange={(e) => updatePageConfig('page_role', e.target.value)}>
            <option value="front_cover">Front Cover</option>
            <option value="inner">Inner</option>
            <option value="back_cover">Back Cover</option>
          </select>
        </div>
        <div className="cell-prop-group">
          <label>Background</label>
          <input type="color" value={currentPageConfig.background_color || '#ffffff'}
            onChange={(e) => updatePageConfig('background_color', e.target.value)} />
        </div>
        <div className="cell-prop-group">
          <label>Header</label>
          <input type="text" value={currentPageConfig.header_text || ''} placeholder="e.g. Weekly Specials"
            onChange={(e) => updatePageConfig('header_text', e.target.value)}
            style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, padding: '3px 8px', width: 160 }} />
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="control-btn" onClick={addSection} style={{ padding: '4px 10px', fontSize: 12 }}>
            + Section
          </button>
        </div>
      </div>

      {/* Sections for current page */}
      {currentPageSections.length > 0 && (
        <div className="sections-bar">
          {currentPageSections.map((section, i) => {
            const sectionGlobalIdx = sections.indexOf(section);
            return (
              <div key={section.section_id} className="section-item">
                <input type="text" value={section.name} placeholder="Section name"
                  onChange={(e) => updateSection(sectionGlobalIdx, 'name', e.target.value)}
                  style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, padding: '3px 6px', width: 120 }} />
                <select value={section.category || ''} onChange={(e) => updateSection(sectionGlobalIdx, 'category', e.target.value)}
                  style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, padding: '3px 6px' }}>
                  <option value="">Any Category</option>
                  <option value="Produce">Produce</option>
                  <option value="Dairy & Eggs">Dairy & Eggs</option>
                  <option value="Meat & Seafood">Meat & Seafood</option>
                  <option value="Bakery">Bakery</option>
                  <option value="Frozen">Frozen</option>
                  <option value="Beverages">Beverages</option>
                  <option value="Snacks">Snacks</option>
                  <option value="Pantry">Pantry</option>
                  <option value="Health & Beauty">Health & Beauty</option>
                  <option value="Household">Household</option>
                  <option value="Deli">Deli</option>
                  <option value="Other">Other</option>
                </select>
                <input type="text" value={(section.cell_indices || []).join(',')} placeholder="Cells (e.g. 0,1,2)"
                  onChange={(e) => updateSection(sectionGlobalIdx, 'cell_indices', e.target.value.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n)))}
                  style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, padding: '3px 6px', width: 100 }} />
                <input type="color" value={section.header_style?.background_color || '#f97316'}
                  onChange={(e) => updateSection(sectionGlobalIdx, 'header_style', { ...section.header_style, background_color: e.target.value })}
                  style={{ width: 24, height: 22 }} />
                <button onClick={() => removeSection(sectionGlobalIdx)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14 }}>x</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Cell Config Panel - shown when a cell is clicked */}
      {editingCellIndex !== null && (
        <div className="cell-config-panel">
          <div className="cell-config-header">
            <span>Cell {editingCellIndex} Config</span>
            <button onClick={() => setEditingCellIndex(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>x</button>
          </div>
          <div className="cell-config-body">
            <div className="cell-prop-group">
              <label>Type</label>
              <select value={getCellConfig(editingCellIndex).cellType || 'product'}
                onChange={(e) => updateCellConfig(editingCellIndex, 'cellType', e.target.value)}>
                <option value="product">Product</option>
                <option value="brand_ad">Brand Ad</option>
                <option value="header">Header</option>
                <option value="filler">Filler</option>
              </select>
            </div>
            <div className="cell-prop-group">
              <label>Category</label>
              <select value={getCellConfig(editingCellIndex).category || ''}
                onChange={(e) => updateCellConfig(editingCellIndex, 'category', e.target.value)}>
                <option value="">Any</option>
                <option value="Produce">Produce</option>
                <option value="Dairy & Eggs">Dairy & Eggs</option>
                <option value="Meat & Seafood">Meat & Seafood</option>
                <option value="Bakery">Bakery</option>
                <option value="Frozen">Frozen</option>
                <option value="Beverages">Beverages</option>
                <option value="Snacks">Snacks</option>
                <option value="Pantry">Pantry</option>
                <option value="Health & Beauty">Health & Beauty</option>
                <option value="Household">Household</option>
                <option value="Deli">Deli</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="editor-content">
        {/* Canvas Area */}
        <div className="canvas-area">
          <div className="canvas-wrapper" style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}>
            <FlyerTemplate
              id='flyer-template'
              grid={grid}
              setGrid={setGrid}
              gridDimensions={gridDimensions}
              selectedCells={selectedCells}
              setSelectedCells={setSelectedCells}
              showCenterGuides={showCenterGuides}
              onCellDoubleClick={handleCellConfigClick}
              cellConfigs={cellConfigs[currentPageIndex] || []}
            />
          </div>
        </div>

        {/* Right Sidebar - Ad Templates */}
        <div className="templates-sidebar">
          <div className="sidebar-header">
            <h2>Ad Templates</h2>
            <p className="sidebar-hint">Drag templates onto grid cells</p>
          </div>
          <div className="templates-list">
            {templates.map((template, index) => (
              <TemplatePreview key={index} template={template} />
            ))}
          </div>
          {templates.length === 0 && (
            <div className="empty-templates">
              <p>No ad templates available</p>
              <button
                className="create-template-link"
                onClick={() => navigate('/adtemplatemanager')}
              >
                Create Ad Template
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="action-bar">
        <button className="action-btn secondary" onClick={handleCancel}>
          Cancel
        </button>
        <button className="action-btn primary" onClick={handleSave}>
          {flyerEditing ? 'Update' : 'Save'}
        </button>
        <button className="action-btn primary-dark" onClick={handleSaveAndClose}>
          {flyerEditing ? 'Update & Close' : 'Save & Close'}
        </button>
        {flyerEditing && (
          <button className="action-btn danger" onClick={handleDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

// Template preview component
function TemplatePreview({ template }) {
  const [{ isDragging }, drag] = useDrag({
    type: 'template',
    item: { id: template._id || template.id, type: 'template' },
    collect: monitor => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  return (
    <div
      className="template-preview-card"
      ref={drag}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {template.preview_image_url && (
        <img src={template.preview_image_url} alt="Template" />
      )}
      <div className="template-preview-label">
        {template.ad_dimensions || 'Template'}
      </div>
    </div>
  );
}

export default FlyerTemplateManager;
